<template>
  <div class="file-upload">
    <el-upload 
      ref="upload"
      class="upload-demo"
      :accept="accept"
      :action="action" 
      :data="uploadData"
      :multiple="multiple"
      :limit="max"
      :auto-upload="false"
      :file-list="fileList"
      :before-upload="onBeforeUpload"
      :on-change="onFileChange"
      :on-success="onSuccess"
      :on-error="onError"
      :on-remove="onDelete"
    >
      <div class="upload-container">
        <slot></slot>
        <div v-if="tips" class="tips">{{ tips }}</div>
      </div>
    </el-upload>
  </div>
</template>

<script>
export default {
  name: 'FileUpload',
  props: {
    multiple: {
      type: Boolean,
      default: false,
    },
    tips: {
      type: String,
      default: '',
    },
    accept: {
      type: String,
      default: '',
    },
    example: {
      type: String,
      default: '',
    },
    max: {
      type: Number,
      default: 9
    },
    // v-model 支持
    value: {
      type: [String, Array],
      default: () => []
    }
  },
  data() {
    return {
      action: '',
      uploadData: {},
      imgList: [],
      fileList: [],
      // 用于存储每个文件的上传参数和URL
      fileUploadMap: new Map(),
      // 正在处理的文件队列
      processingFiles: new Set(),
    }
  },
  watch: {
    // 监听外部 v-model 值的变化
    value: {
      handler(newVal, oldVal) {
        // 避免无限循环：只有当外部值真正改变时才更新内部值
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          if (Array.isArray(newVal)) {
            this.imgList = [...newVal];
          } else if (typeof newVal === 'string') {
            this.imgList = newVal ? [newVal] : [];
          } else {
            this.imgList = [];
          }
        }
      },
      immediate: true
    }
  },
  computed: {
    showAdd() {
      return (this.multiple && this.imgList.length < this.max) || (!this.multiple && !this.imgList.length)
    }
  },
      methods: {
    // 发出 v-model 变更事件
    emitChange(imgList) {
      // 避免无限循环：检查新值是否与当前外部值不同
      const currentValue = this.value;
      let newValue;
      
      if (this.multiple) {
        newValue = [...imgList];
        // 如果是数组，比较内容
        if (JSON.stringify(newValue) !== JSON.stringify(currentValue)) {
          this.$emit('input', newValue);
        }
      } else {
        newValue = imgList.length > 0 ? imgList[0] : '';
        // 如果是字符串，直接比较
        if (newValue !== currentValue) {
          this.$emit('input', newValue);
        }
      }
    },

    onContainerClick(e) {
      if (e.target !== this.$refs.addBtn) {
        e.stopPropagation();
      }
    },

    onBeforeUpload(file) {
      // 检查文件是否已经在处理中
      if (this.processingFiles.has(file.uid)) {
        return false;
      }
      return true;
    },

    onFileChange(file, fileList) {
      console.log('onFileChange', file, fileList);
      if (file.status === 'ready' && !this.processingFiles.has(file.uid)) {
        this.processingFiles.add(file.uid);
        this.getUploadParams(file);
      }
    },

    // 更新文件状态和进度
    updateFileStatus(file, status, percentage) {
      // 更新 fileList 中对应文件的状态
      const fileList = this.$refs.upload.uploadFiles;
      const targetFile = fileList.find(f => f.uid === file.uid);
      if (targetFile) {
        targetFile.status = status;
        targetFile.percentage = percentage;
      }
    },

    getUploadParams(file) {
      console.log('getUploadParams for:', file.name);
      const ext = file.name.split('.').pop();
      const timestamp = Date.now();
      const filename = `${timestamp}_${file.uid}.${ext}`;

      window.lib.api.get({
        api: 'ec/c/tool/upload/auth/get',
        data: {
          filename: filename,
        },
        success: (res) => {
          const { host, policy, signature, access_id, filename: serverFilename, dir, url } = res.data;
          
          // 为每个文件存储独立的上传参数和URL
          this.fileUploadMap.set(file.uid, {
            filename: serverFilename,
            url: url,
            host: host,
            uploadData: {
              policy,
              signature,
              OSSAccessKeyId: access_id,
              success_action_status: '200',
              name: serverFilename,
              key: `${dir}${serverFilename}`,
            }
          });

          // 立即上传这个文件
          this.uploadSingleFile(file);
        },
        error: (err) => {
          this.$message.error(err.msg);
          this.processingFiles.delete(file.uid);
        }
      });
    },

    uploadSingleFile(file) {
      const fileData = this.fileUploadMap.get(file.uid);
      if (!fileData) {
        console.error('未找到文件上传参数:', file.name);
        this.processingFiles.delete(file.uid);
        return;
      }

      // 设置文件状态为 uploading
      this.updateFileStatus(file, 'uploading', 0);
      
      // 使用原生 XMLHttpRequest 直接上传到阿里云OSS
      const formData = new FormData();
      
      // 添加OSS需要的参数
      Object.keys(fileData.uploadData).forEach(key => {
        formData.append(key, fileData.uploadData[key]);
      });
      
      // 添加文件，注意文件参数必须最后添加
      formData.append('file', file.raw);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          // 更新文件进度
          this.updateFileStatus(file, 'uploading', percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          this.onSuccess(xhr.response, file);
        } else {
          this.onError(new Error(`上传失败: ${xhr.status}`), file);
        }
      };

      xhr.onerror = () => {
        console.error('上传出错:', file.name);
        this.onError(new Error('网络错误'), file);
      };

      xhr.open('POST', fileData.host);
      xhr.send(formData);
    },

    onSuccess(response, file) {
      const fileData = this.fileUploadMap.get(file.uid);
      if (!fileData) {
        console.error('未找到文件数据:', file.name);
        return;
      }

      const url = fileData.url;
      
      // 设置文件状态为成功
      this.updateFileStatus(file, 'success', 100);
      
      // 更新 imgList，会自动触发 v-model 更新
      const newImgList = [...this.imgList];
      if (this.multiple) {
        newImgList.push(url);
      } else {
        newImgList.splice(0, newImgList.length, url);
      }
      this.imgList = newImgList;
      
      // 发出 v-model 变更事件
      this.emitChange(newImgList);

      this.$emit('success', url);
      
      // 清理该文件的处理状态
      this.processingFiles.delete(file.uid);
      
      console.log('当前图片列表:', this.imgList);
    },

    onError(error, file) {
      console.error('上传失败:', file.name, error);
      
      // 设置文件状态为失败
      this.updateFileStatus(file, 'fail', 0);
      
      this.$emit('error', error);
      
      // 清理该文件的处理状态和数据
      this.processingFiles.delete(file.uid);
      this.fileUploadMap.delete(file.uid);
    },

    onDelete(file) {
      console.log('删除文件:', file);
      
      let deleteIndex = -1;
      let deleteUrl = '';
      
      if (typeof file === 'number') {
        // 如果传入的是索引
        deleteIndex = file;
        deleteUrl = this.imgList[deleteIndex];
      } else {
        // 如果传入的是文件对象
        const fileData = this.fileUploadMap.get(file.uid);
        if (fileData) {
          deleteUrl = fileData.url;
          deleteIndex = this.imgList.indexOf(deleteUrl);
        }
        
        // 清理文件数据
        this.processingFiles.delete(file.uid);
        this.fileUploadMap.delete(file.uid);
      }
      
      if (deleteIndex >= 0) {
        // 更新 imgList，会自动触发 v-model 更新
        const newImgList = [...this.imgList];
        newImgList.splice(deleteIndex, 1);
        this.imgList = newImgList;
        
        // 发出 v-model 变更事件
        this.emitChange(newImgList);
      }
      
      this.$emit('delete', deleteUrl);
      console.log('删除后图片列表:', this.imgList);
    },

    preview(img) {
      console.log('预览图片:', img);
      // 可以在这里添加图片预览逻辑
    },

    clearFiles() {
      this.imgList = [];
      this.fileUploadMap.clear();
      this.processingFiles.clear();
      this.$refs.upload.clearFiles();
      
      // 发出 v-model 变更事件
      this.emitChange([]);
    },

    // 新增：获取当前已上传的图片列表
    getUploadedImages() {
      return [...this.imgList];
    },

    // 新增：设置初始图片列表（用于编辑模式）
    setInitialImages(images = []) {
      this.imgList = Array.isArray(images) ? [...images] : (images ? [images] : []);
      
      // 发出 v-model 变更事件
      this.emitChange(this.imgList);
    },

    // 新增：添加图片到列表（外部调用）
    addImage(url) {
      if (!url) return;
      const newImgList = [...this.imgList];
      if (this.multiple) {
        if (newImgList.length < this.max) {
          newImgList.push(url);
        }
      } else {
        newImgList.splice(0, newImgList.length, url);
      }
      this.imgList = newImgList;
      
      // 发出 v-model 变更事件
      this.emitChange(newImgList);
    },

    // 新增：移除指定URL的图片
    removeImage(url) {
      if (!url) return;
      const index = this.imgList.indexOf(url);
      if (index >= 0) {
        const newImgList = [...this.imgList];
        newImgList.splice(index, 1);
        this.imgList = newImgList;
        
        // 发出 v-model 变更事件
        this.emitChange(newImgList);
      }
    }
  },
}
</script>

<style lang="less" scoped>
.upload-container {
  width: 100%;
}
</style>
